import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { RatelistType } from "@sinch/core/entities/serverEnums";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RatelistSelectInput } from "@sinch/components/application/Rate/components/RatelistSelectInput/RatelistSelectInput";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { includes } from "ramda";

interface AddRatelistDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  companyId: string;
  type: RatelistType;
  exclude: (string | number)[];
}

export const AddRatelistDialog = ({ open, onClose, onAdded, companyId, type, exclude }: AddRatelistDialogProps) => {
  const { t } = useTranslation(["common", "draft"]);
  const { linkToCompany, loading } = useRatelist();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleConfirm = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const result = await linkToCompany({ variables: { ratelistIds: selectedIds, companyId } });
    if (result.data?.linkRatelistToCompany.result) {
      setSelectedIds([]);
      onAdded();
      onClose();
    }
  }, [selectedIds, linkToCompany, companyId, onAdded, onClose]);

  const handleClose = useCallback(() => {
    setSelectedIds([]);
    onClose();
  }, [onClose]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <DialogTitle>{t("CompanyRatelist.addExisting")}</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography>{t("CompanyRatelist.addExistingDescription")}</Typography>
          <RatelistSelectInput
            companySpecific
            disableCloseOnSelect
            multiple
            name="ratelistSelect"
            onChange={(value) => setSelectedIds((value ?? []) as string[])}
            optionSelector={(options) => options.filter(({ value }) => !includes(value, exclude ?? []))}
            ratelistType={type}
            value={selectedIds}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("Action.close")}</Button>
        <Button
          color="primary"
          disabled={selectedIds.length === 0 || loading}
          onClick={handleConfirm}
          variant="contained"
        >
          {t("CompanyRatelist.addExisting")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
